import React, { useEffect, useState } from 'react';
import { Loader2, Copy, Send, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { oapi, copyText, fmtWhen } from '@/lib/officialsApi';
import { OfficialAvatar, AssignmentStatusBadge } from './StatusPill';
import { AccountPill, Tags } from './OfficialRow';

const AssignmentLine = ({ a }) => (
  <li className="flex items-center gap-3 py-2 text-sm" data-testid={`detail-assignment-${a.id}`}>
    <div className="w-28 shrink-0 text-xs font-semibold text-slate-500">{fmtWhen(a.game)}</div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-slate-800 truncate">{a.game?.team_a_name} vs {a.game?.team_b_name}</div>
      <div className="text-xs text-slate-500 truncate">{a.role_label} · {a.game?.event_name}</div>
    </div>
    <AssignmentStatusBadge status={a.status} />
  </li>
);

const Section = ({ title, children }) => (
  <div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">{title}</div>{children}</div>
);

export const OfficialDetailDialog = ({ open, orgId, officialId, roleLabel, onClose, onEdit, onInvite }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open || !officialId) return;
    setD(null);
    oapi(`/officials/list/${officialId}`, { orgId }).then(setD).catch(e => toast.error(e.message));
  }, [open, officialId, orgId]);
  const o = d?.official;
  const now = new Date().toISOString();
  const all = d?.assignments || [];
  const upcoming = all.filter(a => a.status !== 'cancelled' && (!a.game_end_utc || a.game_end_utc >= now)).reverse();
  const history = all.filter(a => !upcoming.includes(a)).slice(0, 30);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="official-detail-dialog">
        {!o ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-slate-400" /> : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <OfficialAvatar name={o.full_name} photo={o.photo_path} size="w-14 h-14" />
                <div className="min-w-0 text-left">
                  <DialogTitle className="text-[#00247D] text-xl">{o.full_name}</DialogTitle>
                  <DialogDescription>{[o.email, o.phone].filter(Boolean).join(' · ') || 'No contact details'}</DialogDescription>
                  <div className="mt-1 flex items-center gap-2"><AccountPill o={{ ...o, has_account: !!o.user_id }} />
                    {o.status === 'inactive' && <span className="text-[10px] uppercase font-bold text-slate-400">Inactive</span>}</div>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
              <div className="grid sm:grid-cols-2 gap-4">
                <Section title="Sports"><Tags items={o.sports} tone="bg-[#00247D]/10 text-[#00247D]" /></Section>
                <Section title="Positions"><Tags items={(o.positions || []).map(roleLabel)} tone="bg-slate-100 text-slate-600" /></Section>
                <Section title="Certifications"><p className="text-sm text-slate-700">{(o.certifications || []).join(', ') || '—'}</p></Section>
                <Section title="Availability">
                  <p className="text-sm text-slate-700" data-testid="detail-availability">
                    {d.availability?.entries?.length ? `${d.availability.entries.length} entries · updated ${new Date(d.availability.updated_at).toLocaleDateString()}` : 'Not submitted yet'}
                  </p>
                </Section>
              </div>
              {o.notes && <Section title="Notes"><p className="text-sm text-slate-700 whitespace-pre-wrap">{o.notes}</p></Section>}
              {o.invite_link && (
                <Section title="Personal invite link">
                  <p className="text-xs font-mono text-slate-600 break-all bg-slate-50 rounded-md px-2 py-1.5 mb-2" data-testid="detail-invite-link">{o.invite_link}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyText(o.invite_link, 'Invite link')} data-testid="detail-copy-invite"><Copy className="w-4 h-4 mr-1.5" />Copy link</Button>
                    {o.email && <Button variant="outline" size="sm" onClick={() => onInvite(o)} data-testid="detail-send-invite"><Send className="w-4 h-4 mr-1.5" />Email invite</Button>}
                  </div>
                </Section>
              )}
              <Section title={`Upcoming (${upcoming.length})`}>
                {upcoming.length ? <ul className="divide-y">{upcoming.map(a => <AssignmentLine key={a.id} a={a} />)}</ul> : <p className="text-sm text-slate-500">No upcoming games.</p>}
              </Section>
              {history.length > 0 && <Section title="History"><ul className="divide-y">{history.map(a => <AssignmentLine key={a.id} a={a} />)}</ul></Section>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button className="bg-[#00247D] hover:bg-[#001A5C]" onClick={() => onEdit(o)} data-testid="detail-edit-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
